import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, MessageCircle, HelpCircle, ShieldCheck, CreditCard, Baby } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/support" },
      { title: "Support | الدعم — Jozori" },
      {
        name: "description",
        content:
          "Get help with your Jozori account, subscription, child profiles, and content access. We respond within 24 hours.",
      },
      { property: "og:title", content: "Jozori Support — الدعم" },
      {
        property: "og:description",
        content: "Help center for parents using Jozori — accounts, billing, kids' content.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/support" }],
  }),
  component: SupportPage,
});

function SupportPage() {
  const { lang } = useLang();
  const ar = lang === "ar";

  const faqs = ar
    ? [
        { q: "كيف أُلغي اشتراكي؟", a: "من داخل التطبيق: الإعدادات → الاشتراك → إلغاء. أو من إعدادات Apple ID على الآيفون → الاشتراكات." },
        { q: "هل أستطيع استرداد المبلغ؟", a: "نعم — راجع صفحة سياسة الاسترداد. للمشتريات داخل التطبيق من iOS، يتم الاسترداد عبر Apple مباشرةً." },
        { q: "كيف أضيف طفلاً ثانياً؟", a: "من شاشة الأهل → إدارة الأطفال → إضافة طفل. اشتراك Family يدعم حتى 4 أطفال." },
        { q: "نسيت رمز PIN الخاص بالأهل", a: "من الإعدادات → الأمان → إعادة تعيين PIN عبر البريد الإلكتروني." },
        { q: "المحتوى لا يعمل / لا يُحمَّل", a: "تأكد من اتصال الإنترنت ثم أعد تشغيل التطبيق. إذا استمرت المشكلة راسلنا." },
      ]
    : [
        { q: "How do I cancel my subscription?", a: "In-app: Settings → Subscription → Cancel. Or via Apple ID Settings → Subscriptions on iPhone." },
        { q: "Can I get a refund?", a: "Yes — see our refund policy page. For iOS in-app purchases, refunds are processed by Apple." },
        { q: "How do I add a second child?", a: "Parent screen → Manage children → Add child. Family plan supports up to 4 children." },
        { q: "I forgot my parent PIN", a: "Settings → Security → Reset PIN via email." },
        { q: "Content won't load", a: "Check your internet connection and restart the app. If it persists, email us." },
      ];

  const channels = [
    {
      icon: Mail,
      title: ar ? "البريد الإلكتروني" : "Email",
      value: "support@jozori.com",
      href: "mailto:support@jozori.com",
      desc: ar ? "نرد خلال 24 ساعة" : "We reply within 24 hours",
    },
    {
      icon: MessageCircle,
      title: ar ? "نموذج التواصل" : "Contact form",
      value: ar ? "نموذج كامل" : "Full form",
      href: "/contact",
      desc: ar ? "للأسئلة المفصّلة والشراكات" : "For detailed questions & partnerships",
    },
  ];

  const categories = [
    { icon: CreditCard, label: ar ? "الفوترة والاشتراكات" : "Billing & subscriptions" },
    { icon: Baby, label: ar ? "حسابات الأطفال" : "Child accounts" },
    { icon: ShieldCheck, label: ar ? "الأمان والخصوصية" : "Safety & privacy" },
    { icon: HelpCircle, label: ar ? "أسئلة عامة" : "General questions" },
  ];

  return (
    <SiteShell>
      <div className="container mx-auto max-w-4xl px-4 py-16" dir={ar ? "rtl" : "ltr"}>
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {ar ? "مركز الدعم" : "Support Center"}
          </h1>
          <p className="text-lg text-muted-foreground">
            {ar
              ? "نحن هنا لمساعدتك. اختر القناة المناسبة أو ابحث في الأسئلة الشائعة."
              : "We're here to help. Pick a channel or browse common questions."}
          </p>
        </header>

        <section className="grid gap-4 sm:grid-cols-2 mb-12">
          {channels.map((c) => (
            <a
              key={c.title}
              href={c.href}
              className="group rounded-2xl border bg-card p-6 transition hover:border-primary hover:shadow-lg"
            >
              <c.icon className="h-8 w-8 text-primary mb-3" />
              <h3 className="font-semibold text-lg mb-1">{c.title}</h3>
              <p className="text-primary font-medium">{c.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{c.desc}</p>
            </a>
          ))}
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4">
            {ar ? "تصفّح حسب الفئة" : "Browse by category"}
          </h2>
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            {categories.map((c) => (
              <div
                key={c.label}
                className="rounded-xl border bg-muted/30 p-4 text-center"
              >
                <c.icon className="h-6 w-6 mx-auto text-primary mb-2" />
                <span className="text-sm font-medium">{c.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">
            {ar ? "الأسئلة الشائعة" : "Frequently Asked Questions"}
          </h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl border bg-card p-5 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  <span>{f.q}</span>
                  <span className="ml-2 transition group-open:rotate-45 text-2xl text-primary leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-primary/5 border border-primary/20 p-6 text-center">
          <h3 className="font-semibold mb-2">
            {ar ? "لم تجد إجابتك؟" : "Didn't find your answer?"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {ar
              ? "راسلنا مباشرة وسنعود إليك خلال 24 ساعة."
              : "Email us directly and we'll get back within 24 hours."}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition hover:opacity-90"
          >
            {ar ? "تواصل معنا" : "Contact us"}
          </Link>
        </section>
      </div>
    </SiteShell>
  );
}
